import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import AuthenticationForm from "./AuthenticationForm";
import ImportProgressDashboard from "./ImportProgressDashboard";
import ShopifyOAuthCallback from "./components/ShopifyOAuthCallback";
import IframeSessionExpired from "./components/IframeSessionExpired";
import StatusPage from "./components/StatusPage";
import useIframeAuth from "./hooks/useIframeAuth";
import {
  clearSession,
  getSessionState,
  IFRAME_SESSION_EXPIRED_EVENT,
  isInIframe,
} from "./utils/authSession";

const refreshAuthToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  try {
    const response = await axios.post(
      `https://securetoken.googleapis.com/v1/token?key=${process.env.REACT_APP_FIREBASE_API_KEY}`,
      new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    );

    const { id_token, refresh_token } = response.data;
    localStorage.setItem("authToken", id_token);
    localStorage.setItem("refreshToken", refresh_token);
    return id_token;
  } catch {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    return null;
  }
};

const App = () => {
  const location = useLocation();
  const isPublicStatusPage = location.pathname.replace(/\/+$/, "") === "/status";
  const [initialSession] = useState(() => {
    const iframe = isInIframe();
    const sessionState = getSessionState({ iframe });

    return {
      iframe,
      sessionState,
      token:
        sessionState === "active" ? localStorage.getItem("authToken") : null,
    };
  });
  const [apiKey, setApiKey] = useState(() => {
    return initialSession.token;
  });
  const [checkingToken, setCheckingToken] = useState(true);
  const [iframeSessionExpired, setIframeSessionExpired] = useState(
    initialSession.iframe && initialSession.sessionState === "expired"
  );

  const handleTokenReceived = useCallback((token) => {
    setApiKey(token);
    setIframeSessionExpired(false);
    setCheckingToken(false);
  }, []);

  const { isIframe, waitingForIframeAuth } = useIframeAuth(
    handleTokenReceived,
    !iframeSessionExpired
  );

  const handleAuthentication = (key) => {
    setApiKey(key);
  };

  const handleLogout = () => {
    clearSession();
    setApiKey(null);
  };

  useEffect(() => {
    if (initialSession.sessionState !== "active") {
      clearSession();
    }
  }, [initialSession.sessionState]);

  useEffect(() => {
    if (!isIframe) return undefined;

    const handleIframeSessionExpired = () => {
      setApiKey(null);
      setCheckingToken(false);
      setIframeSessionExpired(true);
    };

    window.addEventListener(
      IFRAME_SESSION_EXPIRED_EVENT,
      handleIframeSessionExpired
    );

    return () => {
      window.removeEventListener(
        IFRAME_SESSION_EXPIRED_EVENT,
        handleIframeSessionExpired
      );
    };
  }, [isIframe]);

  useEffect(() => {
    if (iframeSessionExpired) return;
    if (isIframe && waitingForIframeAuth) return;

    const validateOrRefresh = async () => {
      if (getSessionState({ iframe: isIframe }) === "expired") {
        clearSession();
        setApiKey(null);
        if (isIframe) {
          setIframeSessionExpired(true);
        }
        setCheckingToken(false);
        return;
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        setCheckingToken(false);
        return;
      }

      // Firebase ID tokens are JWTs — check if expired
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const isExpired = payload.exp * 1000 < Date.now();

        if (isExpired) {
          if (isIframe) {
            clearSession();
            setApiKey(null);
            setIframeSessionExpired(true);
          } else {
            const newToken = await refreshAuthToken();
            setApiKey(newToken);
          }
        }
      } catch {
        if (isIframe) {
          clearSession();
          setApiKey(null);
          setIframeSessionExpired(true);
        } else {
          // Malformed token — try refresh as fallback
          const newToken = await refreshAuthToken();
          setApiKey(newToken);
        }
      }

      setCheckingToken(false);
    };

    validateOrRefresh();
  }, [iframeSessionExpired, isIframe, waitingForIframeAuth]);

  if (isPublicStatusPage) {
    return (
      <div className="App">
        <StatusPage publicView />
      </div>
    );
  }

  if (iframeSessionExpired) {
    return <IframeSessionExpired />;
  }

  if (checkingToken || waitingForIframeAuth) {
    return <div>Loading...</div>;
  }

  return (
    <div className="App">
      <Routes>
        <Route 
          path="/login" 
          element={
            !apiKey ? (
              <AuthenticationForm onAuthenticated={handleAuthentication} />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          } 
        />
        
        {/* Shopify OAuth Callback Route - Standalone, no auth required */}
        <Route 
          path="/dashboard/ShopifyOAuthCallback" 
          element={<ShopifyOAuthCallback />} 
        />
        
        <Route 
          path="/dashboard/*" 
          element={
            apiKey ? (
              <ImportProgressDashboard apiKey={apiKey} onLogout={handleLogout} isIframe={isIframe} />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        
        <Route 
          path="/" 
          element={<Navigate to={apiKey ? "/dashboard" : "/login"} replace />} 
        />
        
        <Route 
          path="*" 
          element={<Navigate to={apiKey ? "/dashboard" : "/login"} replace />} 
        />
      </Routes>
    </div>
  );
};

export default App;
